import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip"
import type { ColumnDef } from "@tanstack/react-table"
import { useMemo } from "react"
import { useShallow } from "zustand/shallow"

import {
  GoodsReceiptPalletActionsCell,
  GoodsReceiptPalletBatchNumbersCell,
  GoodsReceiptPalletDestinationCell,
  GoodsReceiptPalletLotNumbersCell,
  GoodsReceiptPalletProductIdCell,
  GoodsReceiptPalletProductQtyCell,
  GoodsReceiptPalletReferenceIdCell,
} from "../../../components"
import { calculateGoodsReceiptPalletQuantity, isAkrochemManagedWarehouse } from "../../../utils"

import type { GoodsReceiptPallet } from "@/graphql/codegen/graphql"
import { useAppView } from "@/hooks/useAppView"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import { formatDate } from "@/utils/date"
import { getUomName } from "@/utils/uom"

interface UseStoredGoodsReceiptPalletColumnsParams {
  onQrPrint?: (pallet: GoodsReceiptPallet) => void
}

export const useStoredGoodsReceiptPalletColumns = ({
  onQrPrint,
}: UseStoredGoodsReceiptPalletColumnsParams = {}): ColumnDef<GoodsReceiptPallet>[] => {
  const { isTabletView } = useAppView()
  const uoms = useUnitOfMeasurementsStore(useShallow((s) => s.units))

  const columns = useMemo<ColumnDef<GoodsReceiptPallet>[]>(() => {
    const allColumns: Record<string, ColumnDef<GoodsReceiptPallet>> = {
      productQty: {
        accessorKey: "productQty",
        header: "Product / Qty",
        enableSorting: false,
        cell: ({ row }) => <GoodsReceiptPalletProductQtyCell pallet={row.original} />,
      },
      grNumber: {
        accessorKey: "agrNumber",
        header: "GR # / PO #",
        enableSorting: true,
        cell: ({ row }) => {
          const grNumber = row.original.goodsReceiptPalletRow?.goodsReceipt?.agrNumber
          const poNumber = row.original.goodsReceiptPalletRow?.order?.orderId

          return (
            <Box component='span' className='flex flex-col gap-y-1'>
              <Box component='span' className='font-bold text-primary'>
                {grNumber || "-"}
              </Box>
              <Box component='span'>{poNumber || "-"}</Box>
            </Box>
          )
        },
      },
      referenceId: {
        accessorKey: "referenceId",
        header: "Ref #",
        enableSorting: false,
        cell: ({ row }) => <GoodsReceiptPalletReferenceIdCell pallet={row.original} />,
      },
      productId: {
        accessorKey: "productId",
        header: "Product ID",
        enableSorting: false,
        cell: ({ row }) => <GoodsReceiptPalletProductIdCell pallet={row.original} />,
      },
      description: {
        accessorKey: "description",
        header: "Description",
        enableSorting: false,
        cell: ({ row }) => row.original.goodsReceiptPalletRow?.product?.companyProductDescription ?? "",
      },
      palletQty: {
        accessorKey: "palletQty",
        header: "Qty",
        enableSorting: false,
        cell: ({ row }) =>
          `${calculateGoodsReceiptPalletQuantity(row.original.goodsReceiptPalletRow?.lots ?? [])} ${getUomName(uoms, row.original.goodsReceiptPalletRow?.product?.packageUsWeightUom)}`,
      },
      lots: {
        accessorKey: "lots",
        header: "Lot #",
        enableSorting: false,
        cell: ({ row }) => <GoodsReceiptPalletLotNumbersCell pallet={row.original} />,
      },
      batchNumber: {
        accessorKey: "batchNumber",
        header: "Batch #",
        enableSorting: false,
        cell: ({ row }) => (
          <GoodsReceiptPalletBatchNumbersCell goodsReceiptPalletRow={row.original.goodsReceiptPalletRow!} />
        ),
      },
      storedOn: {
        accessorKey: "updatedOn",
        header: "Stored",
        enableSorting: true,
        cell: ({ row }) => (
          <Chip className='h-6 rounded-sm bg-primary-200 text-xs' label={formatDate(row.original.updatedOn)} />
        ),
      },
      location: {
        accessorKey: "location",
        header: isTabletView ? "Stored Location" : "Location",
        enableSorting: false,
        cell: ({ row }) => <GoodsReceiptPalletDestinationCell pallet={row.original} shouldShowEdit={false} />,
      },
      actions: {
        accessorKey: "actions",
        header: isTabletView ? "QR" : "",
        enableSorting: false,
        cell: ({ row }) => (
          <GoodsReceiptPalletActionsCell
            pallet={row.original}
            shouldShowQrPrint={isAkrochemManagedWarehouse(
              row.original.goodsReceiptPalletRow?.goodsReceipt?.warehouseArea?.type
            )}
            shouldShowDelete={false}
            onQrPrint={() => onQrPrint?.(row.original)}
          />
        ),
      },
    }

    if (isTabletView) {
      return [
        allColumns.productQty,
        allColumns.lots,
        allColumns.batchNumber,
        allColumns.referenceId,
        allColumns.location,
        allColumns.storedOn,
        allColumns.actions,
      ]
    }

    return [
      allColumns.grNumber,
      allColumns.referenceId,
      allColumns.productId,
      allColumns.description,
      allColumns.palletQty,
      allColumns.lots,
      allColumns.batchNumber,
      allColumns.storedOn,
      allColumns.location,
      allColumns.actions,
    ]
  }, [isTabletView, uoms, onQrPrint])

  return columns
}
